import React, { useEffect, useRef } from 'react';

const badges = ['21 CFR Part 11', 'EU Annex 11 & 16', 'GAMP 5'];

function FloatingOrb({ style }) {
  return (
    <div style={{
      position: 'absolute',
      borderRadius: '50%',
      filter: 'blur(80px)',
      pointerEvents: 'none',
      ...style,
    }} />
  );
}

export default function Hero({ onBookDemo }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let w, h;

    const resize = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * 1200,
      y: Math.random() * 600,
      r: Math.random() * 1.5 + 0.3,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      alpha: Math.random() * 0.5 + 0.1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,201,177,${p.alpha})`;
        ctx.fill();
      });
      // Draw connecting lines
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0,201,177,${0.08 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <section id="hero" style={{
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      paddingTop: 80,
      paddingBottom: 80,
      paddingLeft: 24,
      paddingRight: 24,
    }}>
      {/* BG canvas */}
      <canvas ref={canvasRef} style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        opacity: 0.6,
      }} />

      {/* Orbs */}
      <FloatingOrb style={{ width: 600, height: 600, background: 'rgba(0,201,177,0.07)', top: -100, left: -200 }} />
      <FloatingOrb style={{ width: 500, height: 500, background: 'rgba(123,63,228,0.1)', bottom: -100, right: -150 }} />
      <FloatingOrb style={{ width: 300, height: 300, background: 'rgba(245,166,35,0.06)', top: '40%', left: '60%' }} />

      {/* Grid overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
        pointerEvents: 'none',
      }} />

      {/* Content */}
      <div style={{ position: 'relative', textAlign: 'center', maxWidth: 860, zIndex: 1 }}>
        {/* Compliance badges */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 32 }}>
          {badges.map(b => (
            <span key={b} style={{
              fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
              color: '#00C9B1', background: 'rgba(0,201,177,0.1)',
              border: '1px solid rgba(0,201,177,0.2)',
              padding: '4px 12px', borderRadius: 100,
              textTransform: 'uppercase',
            }}>{b}</span>
          ))}
        </div>

        {/* Eyebrow */}
        <p style={{
          fontSize: 13, fontWeight: 600, letterSpacing: '0.12em',
          color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase',
          marginBottom: 20,
          fontFamily: "'JetBrains Mono', monospace",
        }}>GMP Batch-Record Review & Release</p>

        {/* Headline */}
        <h1 style={{
          fontSize: 'clamp(42px, 7vw, 88px)',
          fontWeight: 900,
          lineHeight: 1.0,
          letterSpacing: '-0.04em',
          marginBottom: 28,
          background: 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.7) 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>
          The batch-release<br />
          <span style={{
            background: 'linear-gradient(135deg, #00C9B1 0%, #7B3FE4 60%, #F5A623 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>platform that extends<br />to any extreme.</span>
        </h1>

        {/* Subtext */}
        <p style={{
          fontSize: 'clamp(16px, 2.2vw, 20px)',
          color: 'rgba(255,255,255,0.55)',
          maxWidth: 620,
          margin: '0 auto 48px',
          lineHeight: 1.7,
          fontWeight: 400,
        }}>
          Electronic, review-by-exception batch release for GMP pharma —
          customizable, integration-first, and AI-driven.
        </p>

        {/* Pillars */}
        <div style={{
          display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap',
          marginBottom: 52,
        }}>
          {['Customizable', 'Integration-First', 'AI-Driven', 'Extensible to Any Extreme'].map((p, i) => {
            const colors = ['#00C9B1','#7B3FE4','#F5A623','#00C9B1'];
            return (
              <div key={p} style={{
                fontSize: 13, fontWeight: 600,
                color: colors[i],
                border: `1px solid ${colors[i]}30`,
                background: `${colors[i]}10`,
                padding: '6px 16px', borderRadius: 8,
              }}>{p}</div>
            );
          })}
        </div>

        {/* CTAs */}
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={onBookDemo} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '14px 32px', fontSize: 15, fontWeight: 700,
            background: 'linear-gradient(135deg, #00C9B1, #7B3FE4)',
            color: '#fff', borderRadius: 10,
            boxShadow: '0 4px 24px rgba(0,201,177,0.25)',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,201,177,0.35)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,201,177,0.25)'; }}
          >
            Book a Demo →
          </button>
          <a href="#workflow" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '14px 32px', fontSize: 15, fontWeight: 600,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: 'rgba(255,255,255,0.85)', borderRadius: 10,
            transition: 'background 0.2s, border-color 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
          >
            See the Workflow
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div style={{
        position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        opacity: 0.35,
      }}>
        <span style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#fff' }}>Scroll</span>
        <div style={{
          width: 20, height: 32, border: '1.5px solid rgba(255,255,255,0.4)',
          borderRadius: 10, display: 'flex', justifyContent: 'center', paddingTop: 6,
        }}>
          <div style={{
            width: 3, height: 8, background: '#fff', borderRadius: 2,
            animation: 'scrollDot 1.6s ease-in-out infinite',
          }} />
        </div>
      </div>

      <style>{`
        @keyframes scrollDot {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(8px); opacity: 0.3; }
        }
      `}</style>
    </section>
  );
}
